/* Copyright (c) 2017 Backpack Health LLC */
/* eslint-disable no-underscore-dangle */
/* global window, navigator, cordova, fileChooser, SpinnerDialog */
import * as _ from 'lodash';
import { connect } from 'react-redux';

import Profile from './Profile';
import PhotoModel from '../../../../models/PhotoModel';
import {
  getActiveProfile,
  getActiveProfileId,
  getMainProfileId,
} from '../../redux/reduxSelectors';
import { actions as ownerActions, selectors as ownerSelectors } from '../../../redux/ducks/owners';
import { selectors as listSelectors } from '../../../redux/ducks/lists/lists';

import FileActions from '../../redux/FileActions';
import NavigationActions from '../../redux/NavigationActions';
import ProfileActions from '../../redux/ProfileActions';

import RouteNames from '../../RouteNames';
import GenderModel from '../../../../models/GenderModel';
import ProfilePhrases from '../../../../phraseKeys/Profile.phrase';
import { convertToBlob } from '../../../../utilities/MobileUtil';

const SECONDARY_NAV_LINKS = {
  info: 'list.profileSecondaryNav.info.name',
  settings: 'list.profileSecondaryNav.settings.name',
  account: 'list.profileSecondaryNav.account.name',
  privacy: 'list.profileSecondaryNav.privacy.name',
};

function secondaryNav(isMainProfile, routeName) {
  const nav = [
    { labelData: { id: SECONDARY_NAV_LINKS.info }, value: RouteNames.profileInfo(), isActive: false },
    { labelData: { id: SECONDARY_NAV_LINKS.settings }, value: RouteNames.profileSettings(), isActive: false },
    { labelData: { id: SECONDARY_NAV_LINKS.privacy }, value: RouteNames.profilePrivacy(), isActive: false },
  ];

  if (routeName) {
    const selectedLink = nav.find((link) => link.labelData.id === routeName);
    selectedLink.isActive = true;
  }

  return nav;
}

function selectImageSource(source, onSuccess) {
  const captureError = function captureError() { SpinnerDialog.hide(); };
  const captureSuccess = function captureSuccess(mediaFiles) {
    if (mediaFiles) {
      const path = mediaFiles.url || mediaFiles[0].fullPath || mediaFiles;
      const isContentPath = path.includes('content://');
      const isNotGoogleDrive = !path.includes(
        'com.google.android.apps.docs.storage.legacy'
      );
      if (isContentPath && isNotGoogleDrive) {
        window.FilePath.resolveNativePath(path, captureSuccess, captureError);
      } else if (!isNotGoogleDrive) {
        window.plugins.DocumentContract.getContract(
          { uri: path },
          contract => {
            const googleFileName = contract._display_name;
            window.resolveLocalFileSystemURL(path, file => {
              convertToBlob(file, googleFileName, onSuccess);
            });
          },
          () => {}
        );
      } else {
        window.resolveLocalFileSystemURL(path, file => {
          convertToBlob(file, null, onSuccess);
        });
      }
    }
  };
  if (source === 'library') {
    SpinnerDialog.hide();
    const permissions = cordova.plugins.permissions;
    const success = status => {
      if (status.hasPermission) {
        fileChooser.open(captureSuccess, captureError, 'image/*');
      } else {
        permissions.requestPermission(
          permissions.READ_EXTERNAL_STORAGE,
          success,
          captureError
        );
      }
    };
    permissions.checkPermission(
      permissions.READ_EXTERNAL_STORAGE,
      success,
      captureError
    );
  } else if (source === 'image') {
    navigator.camera.getPicture(captureSuccess, captureError, {
      destinationType: window.Camera.DestinationType.FILE_URI,
      sourceType: window.Camera.PictureSourceType.CAMERA,
      correctOrientation: true,
    });
  }
}

function mapStateToProps(state, routerProps) {
  const profile = getActiveProfile(state);
  const history = routerProps.history;
  const genderCollection = listSelectors.getGenderCollection(state);
  const birthSexCollection = listSelectors.getBirthSexCollection(state);
  const isMainProfile = getActiveProfileId(state) === getMainProfileId(state);

  return {
    profileId: profile.data.id,
    profile,
    isMainProfile,
    isShared: ownerSelectors.getActiveOwnersForProfile(profile.data.id, state).length > 1,
    phraseData: ProfilePhrases.myProfile(isMainProfile, profile.greetingName()),
    currentPage: history.location.pathname,

    gender: GenderModel.createFromId(genderCollection, profile.getGenderId()),
    genderCollection,
    birthSexCollection,
    secondaryNavLinks: SECONDARY_NAV_LINKS,

    secondaryNav,
    doneWithProfile: () => {
      NavigationActions.goBack(history, RouteNames.myBackpack());
    },
    deleteProfile: () => {
      NavigationActions.goTo(history, RouteNames.profileDelete());
    },
    getInitialData: () => {},
    goToLanguages: () => {
      NavigationActions.goTo(history, RouteNames.setPreferredLanguage());
    },
    actions: {
      selectSecondaryNav: routeName => {
        NavigationActions.goTo(history, routeName);
      },
      selectPhoto: (rawFile, cb) => {
        PhotoModel.createFromRawFile(rawFile, true, newPhoto => {
          cb();
          if (newPhoto.isDimensionTooSmall()) {
            NavigationActions.goTo(history, RouteNames.profilePhotoError());
            return;
          }

          newPhoto.restrictToMaxWidth(1200, squareImage => {
            FileActions.rawPhotoSelected(squareImage);
            NavigationActions.goTo(history, RouteNames.profilePhotoCrop());
          });
        });
      },
      selectMobilePhoto: (source, cb) => {
        SpinnerDialog.show();
        selectImageSource(source, rawFile => {
          PhotoModel.createFromRawFile(rawFile, true, newPhoto => {
            cb();
            if (newPhoto.isDimensionTooSmall()) {
              NavigationActions.goTo(history, RouteNames.profilePhotoError());
              SpinnerDialog.hide();
              return;
            }

            newPhoto.restrictToMaxWidth(1200, squareImage => {
              FileActions.rawPhotoSelected(squareImage);
              NavigationActions.goTo(history, RouteNames.profilePhotoCrop());
              SpinnerDialog.hide();
            });
          });
        });
      },
      deletePhoto: () => {
        const profileWithValidValues = _.pickBy(
          profile.data,
          val => val !== null
        );
        ProfileActions.api.deleteAvatar(profileWithValidValues);
      },
      confirmDeleteProfile: () => {
        NavigationActions.goTo(history, RouteNames.profileDelete());
      },
      goToProfilePage: routeName => {
        NavigationActions.goTo(history, routeName);
      },
    },
  };
}

function mapDispatchToProps(dispatch) {
  return {
    loadProfileData: profileId => {
      dispatch(ownerActions.requestOwnersForProfile(profileId));
    },
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(Profile);
